'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { getAllFixtures, getFlag, type LiveFixture } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';
import { History, MapPin, Search, ChevronDown, Trophy } from 'lucide-react';

const FINISHED_STATUSES = ['FT', 'AET', 'PEN', 'AWD', 'WO'];

function getResultLabel(fix: LiveFixture): 'home' | 'away' | 'draw' {
  const h = fix.goals.home ?? 0;
  const a = fix.goals.away ?? 0;
  if (h > a) return 'home';
  if (a > h) return 'away';
  return 'draw';
}

function ScoreBadge({ fix }: { fix: LiveFixture }) {
  const h = fix.goals.home ?? 0;
  const a = fix.goals.away ?? 0;
  const pen = fix.score?.penalty;
  const hasPen = pen && (pen.home !== null || pen.away !== null);
  const statusShort = fix.fixture.status.short;

  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-lg tabular-nums"
        style={{ background: 'hsl(222 35% 14%)', color: 'white' }}
      >
        <span>{h}</span>
        <span className="text-slate-500 text-sm">–</span>
        <span>{a}</span>
      </div>
      <span
        className="text-xs font-semibold px-2 py-0.5 rounded-full"
        style={{
          background: statusShort === 'FT' ? 'hsl(142 71% 45% / 0.12)' : 'hsl(25 95% 53% / 0.12)',
          color: statusShort === 'FT' ? 'hsl(142 71% 55%)' : 'hsl(25 95% 65%)',
        }}
      >
        {hasPen ? `PEN (${pen.home}–${pen.away})` : statusShort}
      </span>
    </div>
  );
}

function MatchCard({ fix }: { fix: LiveFixture }) {
  const result = getResultLabel(fix);
  const matchDate = new Date(fix.fixture.date);
  const { t, language } = useLanguage();
  const dateStr = matchDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Link href={`/game-detail?id=${fix.fixture.id}`}>
      <div
        className="rounded-xl p-4 transition-all duration-200 hover:scale-[1.01] cursor-pointer group"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 15%)' }}
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-slate-500">{fix.league.round}</span>
          <span className="text-xs text-slate-600">{dateStr}</span>
        </div>

        {/* Teams + Score */}
        <div className="flex items-center gap-3">
          {/* Home team */}
          <div className={`flex flex-col items-center gap-1 flex-1 ${result === 'home' ? 'opacity-100' : 'opacity-60'}`}>
            {fix.teams.home.logo ? (
              <img src={fix.teams.home.logo} alt={fix.teams.home.name} className="w-9 h-9 object-contain" />
            ) : (
              <span className="text-2xl">{getFlag(fix.teams.home.name)}</span>
            )}
            <span className="text-xs font-semibold text-slate-300 text-center truncate w-full">
              {fix.teams.home.code ?? fix.teams.home.name.slice(0, 3).toUpperCase()}
            </span>
            {result === 'home' && (
              <span className="text-xs font-bold" style={{ color: 'hsl(142 71% 55%)' }}>{t.common.win}</span>
            )}
          </div>

          {/* Score */}
          <ScoreBadge fix={fix} />

          {/* Away team */}
          <div className={`flex flex-col items-center gap-1 flex-1 ${result === 'away' ? 'opacity-100' : 'opacity-60'}`}>
            {fix.teams.away.logo ? (
              <img src={fix.teams.away.logo} alt={fix.teams.away.name} className="w-9 h-9 object-contain" />
            ) : (
              <span className="text-2xl">{getFlag(fix.teams.away.name)}</span>
            )}
            <span className="text-xs font-semibold text-slate-300 text-center truncate w-full">
              {fix.teams.away.code ?? fix.teams.away.name.slice(0, 3).toUpperCase()}
            </span>
            {result === 'away' && (
              <span className="text-xs font-bold" style={{ color: 'hsl(142 71% 55%)' }}>{t.common.win}</span>
            )}
          </div>
        </div>

        {/* Venue */}
        {fix.fixture.venue.name && (
          <div className="mt-3 flex items-center gap-1 text-xs text-slate-600">
            <MapPin size={10} />
            <span className="truncate">
              {fix.fixture.venue.name}{fix.fixture.venue.city ? `, ${fix.fixture.venue.city}` : ''}
            </span>
          </div>
        )}

        <div className="mt-2 text-right">
          <span className="text-xs text-slate-600 group-hover:text-cyan-400 transition-colors">{t.common.matchDetails}</span>
        </div>
      </div>
    </Link>
  );
}

export default function PastGamesPage() {
  const [fixtures, setFixtures] = useState<LiveFixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roundFilter, setRoundFilter] = useState('All');
  const [showRoundDropdown, setShowRoundDropdown] = useState(false);
  const { t } = useLanguage();

  const fetchPastGames = useCallback(async () => {
    try {
      const all = await getAllFixtures();
      const finished = all.filter(f => FINISHED_STATUSES.includes(f.fixture.status.short));
      // Sort newest first
      finished.sort((a, b) => b.fixture.timestamp - a.fixture.timestamp);
      setFixtures(finished);
    } catch {
      setFixtures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPastGames();
  }, [fetchPastGames]);

  // Unique rounds for filter
  const rounds = ['All', ...Array.from(new Set(fixtures.map(f => f.league.round)))];

  const filtered = fixtures.filter(f => {
    const matchesSearch =
      search === '' ||
      f.teams.home.name.toLowerCase().includes(search.toLowerCase()) ||
      f.teams.away.name.toLowerCase().includes(search.toLowerCase());
    const matchesRound = roundFilter === 'All' || f.league.round === roundFilter;
    return matchesSearch && matchesRound;
  });

  // Group by round
  const grouped: Record<string, LiveFixture[]> = {};
  filtered.forEach(f => {
    if (!grouped[f.league.round]) grouped[f.league.round] = [];
    grouped[f.league.round].push(f);
  });

  return (
    <AppLayout activeRoute="/past-games">
      <div className="space-y-6 pb-20 lg:pb-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <History size={20} className="text-orange-400" />
              {t.pastGames.title}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">{t.pastGames.subtitle}</p>
          </div>
          {!loading && (
            <div
              className="text-xs font-medium px-3 py-1.5 rounded-full"
              style={{ background: 'hsl(222 35% 14%)', color: 'hsl(222 15% 60%)' }}
            >
              {fixtures.length} {t.pastGames.matchesPlayed}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder={t.pastGames.searchTeams}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-orange-500/40 transition-all"
              style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
            />
          </div>

          {/* Round filter */}
          <div className="relative">
            <button
              onClick={() => setShowRoundDropdown(v => !v)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:text-white transition-colors whitespace-nowrap"
              style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
            >
              <Trophy size={14} className="text-slate-500" />
              {roundFilter === 'All' ? t.pastGames.allRounds : roundFilter}
              <ChevronDown size={14} className="text-slate-500" />
            </button>
            {showRoundDropdown && (
              <div
                className="absolute right-0 top-full mt-1 z-20 rounded-xl overflow-hidden shadow-xl min-w-[180px]"
                style={{ background: 'hsl(222 40% 10%)', border: '1px solid hsl(222 25% 16%)' }}
              >
                {rounds.map(r => (
                  <button
                    key={r}
                    onClick={() => { setRoundFilter(r); setShowRoundDropdown(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      roundFilter === r ? 'text-orange-400 bg-orange-500/10' : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {r === 'All' ? t.pastGames.allRounds : r}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map(g => (
              <div key={g}>
                <div className="h-5 w-32 rounded-lg animate-pulse mb-3" style={{ background: 'hsl(222 35% 14%)' }} />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="rounded-xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 160 }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : fixtures.length === 0 ? (
          <div
            className="rounded-2xl p-12 flex flex-col items-center gap-4 text-center"
            style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 15%)' }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'hsl(222 35% 14%)' }}
            >
              <History size={28} className="text-slate-500" />
            </div>
            <div>
              <p className="text-base font-semibold text-white">{t.pastGames.noCompleted}</p>
              <p className="text-sm text-slate-500 mt-1">{t.pastGames.noCompletedSub}</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-2xl p-10 flex flex-col items-center gap-3 text-center"
            style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 15%)' }}
          >
            <Search size={24} className="text-slate-500" />
            <p className="text-sm text-slate-400">{t.pastGames.noResults}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([round, games]) => (
              <div key={round}>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-sm font-semibold text-white">{round}</h2>
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full text-slate-400"
                    style={{ background: 'hsl(222 25% 18%)' }}
                  >
                    {games.length}
                  </span>
                  <div className="flex-1 h-px" style={{ background: 'hsl(222 25% 16%)' }} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {games.map(fix => (
                    <MatchCard key={fix.fixture.id} fix={fix} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
